import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  IconButton,
  Backdrop,
  Fade,
  Popper,
  Paper,
  LinearProgress,
  Chip,
  Portal,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';

interface TourStep {
  target: string;
  title?: string;
  content: string | React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: () => void;
  route?: string;
}

interface GuidedTourProps {
  children?: React.ReactNode;
}

const getTourSteps = (userRole: string): TourStep[] => {
  const baseSteps: TourStep[] = [
    {
      target: 'body',
      title: 'Welcome to OS Workflow Platform! 🎉',
      content: 'Let\'s take a quick tour to help you get started. This platform helps manage medical workflows, from patient screening to study completion.',
      placement: 'center',
    },
    {
      target: '.MuiDrawer-paper',
      title: 'Navigation Menu',
      content: 'Use this sidebar to navigate between different sections of the platform.',
      placement: 'right',
    },
    {
      target: '[href="/worklist"]',
      title: 'Study Worklist',
      content: 'The Study Worklist shows all active studies and their current status. You can filter, sort, and take actions on studies here.',
      placement: 'right',
      route: '/worklist',
    },
  ];

  const supervisorSteps: TourStep[] = [
    {
      target: '[href="/studio"]',
      title: 'Workflow Studio',
      content: 'Design and manage clinical workflows with a visual editor. Create states, transitions, and rules for your processes.',
      placement: 'right',
      route: '/studio',
    },
    {
      target: '[href="/ai-designer"]',
      title: 'AI Workflow Designer ✨',
      content: 'Upload protocol documents and let AI automatically generate workflows for you! It extracts states, transitions, and rules from your documentation.',
      placement: 'right',
      route: '/ai-designer',
    },
    {
      target: '[href="/system-sources"]',
      title: 'System Sources',
      content: 'Configure connections to external systems like EMR, PACS, and RIS. Define how data flows into your workflows.',
      placement: 'right',
      route: '/system-sources',
    },
  ];

  const adminSteps: TourStep[] = [
    {
      target: '[href="/admin"]',
      title: 'Admin Dashboard',
      content: 'The Admin Dashboard provides system metrics, user management, and configuration options.',
      placement: 'right',
      route: '/admin',
    },
  ];

  // Page-specific steps
  const pageSteps: { [key: string]: TourStep[] } = {
    '/studio': [
      {
        target: 'body',
        title: 'Workflow Studio',
        content: 'Welcome to the Workflow Studio! Here you can create and manage your clinical workflows.',
        placement: 'center',
      },
      {
        target: 'button:has-text("New Workflow")',
        title: 'Create New Workflow',
        content: 'Click here to create a new workflow from scratch.',
        placement: 'bottom',
      },
      {
        target: 'button:has-text("Import")',
        title: 'Import Workflows',
        content: 'Import workflow configurations from JSON files.',
        placement: 'bottom',
      },
      {
        target: 'button:has-text("AI Designer")',
        title: 'AI-Powered Design',
        content: 'Use AI to generate workflows from documentation - try this for a magical experience! 🪄',
        placement: 'bottom',
      },
    ],
    '/ai-designer': [
      {
        target: 'body',
        title: 'AI Workflow Designer',
        content: 'The AI Designer can analyze your documents and automatically create workflows!',
        placement: 'center',
      },
      {
        target: 'button:has-text("New AI Design")',
        title: 'Start AI Design',
        content: 'Click here to start a new AI-powered workflow design.',
        placement: 'left',
      },
    ],
    '/system-sources': [
      {
        target: 'body',
        title: 'System Sources',
        content: 'Manage your data source connections here.',
        placement: 'center',
      },
      {
        target: 'button:has-text("Add System Source")',
        title: 'Add New Source',
        content: 'Add new data sources like EMR systems, PACS, or custom APIs.',
        placement: 'left',
      },
    ],
    '/worklist': [
      {
        target: 'body',
        title: 'Study Worklist',
        content: 'View and manage all active studies in your system.',
        placement: 'center',
      },
    ],
    '/admin': [
      {
        target: 'body',
        title: 'Admin Dashboard',
        content: 'Monitor system health and manage platform settings.',
        placement: 'center',
      },
    ],
  };

  // Combine steps based on user role
  let steps = [...baseSteps];
  
  if (userRole === 'supervisor' || userRole === 'admin') {
    steps = [...steps, ...supervisorSteps];
  }
  
  if (userRole === 'admin') {
    steps = [...steps, ...adminSteps];
  }

  return steps;
};

export const GuidedTourCustom: React.FC<GuidedTourProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPrompt, setShowPrompt] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  // Check if user has seen the tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    const tourDismissed = localStorage.getItem('tourDismissed');
    
    if (!hasSeenTour && !tourDismissed && user) {
      setTimeout(() => setShowPrompt(true), 1000);
    }
  }, [user]);

  // Get current steps
  const getSteps = (): TourStep[] => {
    const role = user?.role || 'resident';
    const steps = getTourSteps(role);
    return steps;
  };

  const steps = getSteps();
  const currentStepData = steps[currentStep];

  // Find target element
  useEffect(() => {
    if (isActive && currentStepData) {
      const findElement = () => {
        let element: HTMLElement | null = null;
        
        if (currentStepData.target === 'body') {
          element = document.body;
        } else if (currentStepData.target.includes(':has-text')) {
          // Custom selector for finding by text
          const [selector, text] = currentStepData.target.split(':has-text("');
          const searchText = text.replace('")', '');
          const elements = document.querySelectorAll(selector || 'button');
          elements.forEach((el) => {
            if (el.textContent?.includes(searchText)) {
              element = el as HTMLElement;
            }
          });
        } else {
          element = document.querySelector(currentStepData.target);
        }
        
        setTargetElement(element);
      };

      // Try to find element immediately and after a delay
      findElement();
      const timer = setTimeout(findElement, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, currentStepData, location.pathname]);

  const startTour = () => {
    setShowPrompt(false);
    setIsActive(true);
    setCurrentStep(0);
  };

  const dismissTour = () => {
    setShowPrompt(false);
    localStorage.setItem('tourDismissed', 'true');
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStepData = steps[currentStep + 1];
      
      // Navigate to route if needed
      if (nextStepData.route && location.pathname !== nextStepData.route) {
        navigate(nextStepData.route);
        setTimeout(() => setCurrentStep(currentStep + 1), 500);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setIsActive(false);
    setTourCompleted(true);
    localStorage.setItem('hasSeenTour', 'true');
    setTimeout(() => setTourCompleted(false), 5000);
  };

  const skipTour = () => {
    setIsActive(false);
    localStorage.setItem('hasSeenTour', 'true');
  };

  const resetTour = () => {
    localStorage.removeItem('hasSeenTour');
    localStorage.removeItem('tourDismissed');
    setTourCompleted(false);
    setShowPrompt(true);
  };

  const getPlacement = () => {
    const placement = currentStepData?.placement || 'bottom';
    if (currentStepData?.target === 'body') return 'center';
    return placement;
  };

  const shouldShowOverlay = isActive && currentStepData?.target !== 'body';

  return (
    <>
      {children}

      {/* Tour Overlay */}
      <Portal>
        {shouldShowOverlay && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              pointerEvents: isActive ? 'auto' : 'none',
            }}
          >
            {/* Backdrop with spotlight */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'auto',
              }}
              onClick={skipTour}
            />
            
            {/* Spotlight */}
            {targetElement && (
              <Box
                sx={{
                  position: 'absolute',
                  ...(() => {
                    const rect = targetElement.getBoundingClientRect();
                    return {
                      top: rect.top - 8,
                      left: rect.left - 8,
                      width: rect.width + 16,
                      height: rect.height + 16,
                    };
                  })(),
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                  borderRadius: 1,
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        )}

        {/* Tour Content */}
        {isActive && (
          currentStepData?.target === 'body' ? (
            // Center placement
            <Fade in={isActive}>
              <Box
                sx={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1500,
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: 3,
                    maxWidth: 400,
                    width: '90vw',
                  }}
                >
                  <TourContent
                    step={currentStepData}
                    currentIndex={currentStep}
                    totalSteps={steps.length}
                    onNext={nextStep}
                    onPrev={prevStep}
                    onSkip={skipTour}
                    isFirst={currentStep === 0}
                    isLast={currentStep === steps.length - 1}
                  />
                </Paper>
              </Box>
            </Fade>
          ) : (
            // Popper placement
            targetElement && (
              <Popper
                open={true}
                anchorEl={targetElement}
                placement={getPlacement() as any}
                ref={popperRef}
                sx={{ zIndex: 1500 }}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 16],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                ]}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: 3,
                    maxWidth: 380,
                    width: '90vw',
                  }}
                >
                  <TourContent
                    step={currentStepData}
                    currentIndex={currentStep}
                    totalSteps={steps.length}
                    onNext={nextStep}
                    onPrev={prevStep}
                    onSkip={skipTour}
                    isFirst={currentStep === 0}
                    isLast={currentStep === steps.length - 1}
                  />
                </Paper>
              </Popper>
            )
          )
        )}
      </Portal>

      {/* Tour Prompt */}
      <Portal>
        <Fade in={showPrompt}>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1400,
            }}
          >
            <Card sx={{ maxWidth: 320, boxShadow: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SchoolIcon color="primary" />
                  <Typography variant="h6">
                    Welcome to OS Workflow!
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Would you like a quick tour of the platform? It only takes 2 minutes.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={dismissTour}>
                  Maybe Later
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={startTour}
                >
                  Start Tour
                </Button>
              </CardActions>
            </Card>
          </Box>
        </Fade>
      </Portal>

      {/* Tour Complete Message */}
      <Portal>
        <Fade in={tourCompleted}>
          <Box
            sx={{
              position: 'fixed',
              top: 80,
              right: 24,
              zIndex: 1400,
            }}
          >
            <Card sx={{ maxWidth: 300, bgcolor: 'success.light' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckIcon color="success" />
                  <Typography variant="subtitle1">
                    Tour Complete!
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ ml: 'auto' }}
                    onClick={() => setTourCompleted(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  You can restart the tour anytime from the help menu.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Portal>

      {/* Reset Tour Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 1300,
        }}
      >
        <IconButton
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'background.paper',
              boxShadow: 3,
            },
          }}
          onClick={resetTour}
          title="Restart Tour"
        >
          <SchoolIcon />
        </IconButton>
      </Box>
    </>
  );
};

// Tour Content Component
interface TourContentProps {
  step: TourStep;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const TourContent: React.FC<TourContentProps> = ({
  step,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isFirst,
  isLast,
}) => {
  return (
    <>
      {/* Progress */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" color="text.secondary">
            Step {currentIndex + 1} of {totalSteps}
          </Typography>
          <IconButton size="small" onClick={onSkip}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(currentIndex + 1) / totalSteps * 100}
          sx={{ height: 4, borderRadius: 2 }}
        />
      </Box>

      {/* Content */}
      {step.title && (
        <Typography variant="h6" gutterBottom>
          {step.title}
        </Typography>
      )}
      
      <Box sx={{ mb: 3 }}>
        {typeof step.content === 'string' ? (
          <Typography variant="body2" color="text.secondary">
            {step.content}
          </Typography>
        ) : (
          step.content
        )}
      </Box>

      {/* Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          size="small"
          onClick={onSkip}
          startIcon={<SkipIcon />}
        >
          Skip Tour
        </Button>
        
        <Box display="flex" gap={1}>
          <Button
            size="small"
            onClick={onPrev}
            disabled={isFirst}
            startIcon={<BackIcon />}
          >
            Back
          </Button>
          
          <Button
            size="small"
            variant="contained"
            onClick={onNext}
            endIcon={!isLast && <NextIcon />}
          >
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </>
  );
};